import { BASE_URL } from "./config";

// =====================================================
// COMMON NO CACHE HEADERS
// =====================================================

const noCacheHeaders = {
  "Cache-Control":
    "no-cache, no-store, must-revalidate",

  Pragma:
    "no-cache",

  Expires:
    "0",
};

// =====================================================
// HR HEADERS
// =====================================================

const hrHeaders = (user) => ({
  ...noCacheHeaders,
  "X-User-Email": user?.email || "",
});

// =====================================================
// GET ACTIVE USERS (OPTIMIZED)
//
// CHANGES:
// - Paginated endpoint (default 50 per page)
// - Lean column selection on backend
// - Caches results for 2 minutes
// =====================================================

let userCacheTime = 0;
let userCache = null;
const USER_CACHE_DURATION = 2 * 60 * 1000; // 2 minutes

export const getUsers = async (page = 1, search = "") => {
  const now = Date.now();

  // =================================================
  // CACHE HIT
  // =================================================

  if (
    userCache &&
    !search &&
    now - userCacheTime < USER_CACHE_DURATION
  ) {
    return userCache;
  }

  try {
    const params = new URLSearchParams();

    params.append("page", page);
    params.append("limit", 50);

    if (search) {
      params.append("search", search);
    }

    params.append("t", Date.now());

    const res = await fetch(
      `${BASE_URL}/users?${params.toString()}`,
      {
        method: "GET",
        cache: "no-store",
        headers: noCacheHeaders,
      }
    );

    if (!res.ok) {
      throw new Error(
        `Failed to fetch users: ${res.status}`
      );
    }

    const result = await res.json();

    const users = Array.isArray(result)
      ? result
      : Array.isArray(result?.users)
        ? result.users
        : Array.isArray(result?.data)
          ? result.data
          : [];

    // =================================================
    // UPDATE CACHE (only if no search)
    // =================================================

    if (!search) {
      userCache = result;
      userCacheTime = now;
    }

    return result;
  } catch (error) {
    console.error("Failed to load users:", error);
    return { users: [], pagination: { total_count: 0 } };
  }
};

// =====================================================
// SIGNUP
// =====================================================

export const signup = (data) =>
  fetch(`${BASE_URL}/signup`, {
    method: "POST",

    headers: {
      "Content-Type":
        "application/json",
    },

    body: JSON.stringify(data),
  }).then((res) => res.json());

// =====================================================
// LOGIN
// =====================================================

export const login = (data) =>
  fetch(`${BASE_URL}/login`, {
    method: "POST",

    headers: {
      "Content-Type":
        "application/json",
    },

    body: JSON.stringify(data),
  }).then((res) => res.json());

// =====================================================
// GET ACTIVE LEAVE TYPES
// =====================================================

export const getLeaveTypes =
  async () => {
    const res = await fetch(
      `${BASE_URL}/leave_requests/leave_types?t=${Date.now()}`,
      {
        method: "GET",

        cache: "no-store",

        headers:
          noCacheHeaders,
      }
    );

    return res.json();
  };

// =====================================================
// APPLY LEAVE
// =====================================================

export const applyLeave =
  async (data) => {
    const user =
      JSON.parse(
        localStorage.getItem(
          "user"
        )
      );

    // =================================================
    // FORM DATA FLOW
    // =================================================

    if (
      data instanceof FormData
    ) {
      data.append(
        "user_id",
        user.id
      );

      return fetch(
        `${BASE_URL}/leave_requests`,
        {
          method: "POST",

          body: data,
        }
      ).then(async (res) => {
        const json =
          await res.json();

        if (!res.ok) {
          throw json;
        }

        return json;
      });
    }

    // =================================================
    // JSON FLOW
    // =================================================

    return fetch(
      `${BASE_URL}/leave_requests`,
      {
        method: "POST",

        headers: {
          "Content-Type":
            "application/json",
        },

        body: JSON.stringify({
          ...data,
          user_id:
            user.id,
        }),
      }
    ).then(async (res) => {
      const json =
        await res.json();

      if (!res.ok) {
        throw json;
      }

      return json;
    });
  };

// =====================================================
// USER LEAVES
// =====================================================

export const getLeaves =
  async (userIds = null) => {
    const currentUser =
      JSON.parse(
        localStorage.getItem(
          "user"
        )
      );

    // =================================================
    // MULTIPLE USERS
    // =================================================

    if (
      Array.isArray(userIds)
    ) {
      const ids =
        userIds
          .map((id) =>
            Number(id)
          )
          .filter(
            (id) =>
              Number.isFinite(
                id
              )
          );

      if (
        ids.length === 0
      ) {
        return [];
      }

      try {
        const responses =
          await Promise.all(
            ids.map(
              async (userId) => {
                const res =
                  await fetch(
                    `${BASE_URL}/leave_requests?user_id=${userId}&t=${Date.now()}`,
                    {
                      method:
                        "GET",

                      cache:
                        "no-store",

                      headers:
                        noCacheHeaders,
                    }
                  );

                if (!res.ok) {
                  console.error(
                    `Failed to fetch leaves for user ${userId}`
                  );

                  return [];
                }

                const result =
                  await res.json();

                if (
                  !Array.isArray(
                    result
                  )
                ) {
                  return [];
                }

                return result.filter(
                  (leave) =>
                    Number(
                      leave.user_id
                    ) ===
                    Number(
                      userId
                    )
                );
              }
            )
          );

        return responses.flat();
      } catch (error) {
        console.error(
          "Failed to fetch leaves for selected users:",
          error
        );

        return [];
      }
    }

    // =================================================
    // OLD SINGLE USER FLOW
    // =================================================

    if (
      !currentUser ||
      !currentUser.id
    ) {
      console.error(
        "User not found"
      );

      return [];
    }

    const res =
      await fetch(
        `${BASE_URL}/leave_requests?user_id=${currentUser.id}&t=${Date.now()}`,
        {
          method: "GET",

          cache: "no-store",

          headers:
            noCacheHeaders,
        }
      );

    const result =
      await res.json();

    if (
      !Array.isArray(result)
    ) {
      return [];
    }

    return result.filter(
      (leave) =>
        Number(
          leave.user_id
        ) ===
        Number(
          currentUser.id
        )
    );
  };

// =====================================================
// GET LEAVE BALANCE
// =====================================================

export const getLeaveBalance =
  async (userIds = null) => {
    const currentUser =
      JSON.parse(
        localStorage.getItem(
          "user"
        )
      );

    // =================================================
    // MULTIPLE USERS
    // =================================================

    if (
      Array.isArray(userIds)
    ) {
      const ids =
        userIds
          .map((id) =>
            Number(id)
          )
          .filter(
            (id) =>
              Number.isFinite(
                id
              )
          );

      if (
        ids.length === 0
      ) {
        return {
          total_balance: 0,
          planned_balance: 0,
          unplanned_balance: 0,
        };
      }

      try {
        const balances =
          await Promise.all(
            ids.map(
              async (userId) => {
                try {
                  const res =
                    await fetch(
                      `${BASE_URL}/leave_requests/balance?user_id=${userId}&t=${Date.now()}`,
                      {
                        method:
                          "GET",

                        cache:
                          "no-store",

                        headers:
                          noCacheHeaders,
                      }
                    );

                  if (
                    !res.ok
                  ) {
                    console.error(
                      `Failed to fetch balance for user ${userId}`
                    );

                    return null;
                  }

                  return await res.json();
                } catch (error) {
                  console.error(
                    `Balance request failed for user ${userId}:`,
                    error
                  );

                  return null;
                }
              }
            )
          );

        // =================================================
        // AGGREGATE SELECTED USERS
        // =================================================

        return balances.reduce(
          (
            total,
            balance
          ) => {
            if (
              !balance
            ) {
              return total;
            }

            total.total_balance +=
              Number(
                balance.total_balance ||
                  0
              );

            total.planned_balance +=
              Number(
                balance.planned_balance ||
                  0
              );

            total.unplanned_balance +=
              Number(
                balance.unplanned_balance ||
                  0
              );

            return total;
          },
          {
            total_balance: 0,
            planned_balance: 0,
            unplanned_balance: 0,
          }
        );
      } catch (error) {
        console.error(
          "Failed to fetch balances for selected users:",
          error
        );

        return {
          total_balance: 0,
          planned_balance: 0,
          unplanned_balance: 0,
        };
      }
    }

    // =================================================
    // OLD SINGLE USER FLOW
    // =================================================

    if (
      !currentUser ||
      !currentUser.id
    ) {
      console.error(
        "User not found"
      );

      return {
        total_balance: 0,
        planned_balance: 0,
        unplanned_balance: 0,
      };
    }

    const res =
      await fetch(
        `${BASE_URL}/leave_requests/balance?user_id=${currentUser.id}&t=${Date.now()}`,
        {
          method: "GET",

          cache: "no-store",

          headers:
            noCacheHeaders,
        }
      );

    return res.json();
  };

// =====================================================
// GET LEAVE TRANSACTIONS
// =====================================================

export const getLeaveTransactions =
  async (userIds = null) => {

    // =================================================
    // MULTIPLE USERS
    // =================================================

    if (
      Array.isArray(userIds)
    ) {
      const ids =
        userIds
          .map((id) =>
            Number(id)
          )
          .filter(
            (id) =>
              Number.isFinite(
                id
              )
          );

      if (
        ids.length === 0
      ) {
        return [];
      }

      try {
        const responses =
          await Promise.all(
            ids.map(
              async (userId) => {
                try {
                  const res =
                    await fetch(
                      `${BASE_URL}/leave_requests/transactions?user_id=${userId}&t=${Date.now()}`,
                      {
                        method:
                          "GET",

                        cache:
                          "no-store",

                        headers:
                          noCacheHeaders,
                      }
                    );

                  if (
                    !res.ok
                  ) {
                    console.error(
                      `Failed to fetch transactions for user ${userId}`
                    );

                    return [];
                  }

                  const result =
                    await res.json();

                  return Array.isArray(
                    result
                  )
                    ? result
                    : [];
                } catch (error) {
                  console.error(
                    `Transaction request failed for user ${userId}:`,
                    error
                  );

                  return [];
                }
              }
            )
          );

        return responses.flat();
      } catch (error) {
        console.error(
          "Failed to fetch transactions:",
          error
        );

        return [];
      }
    }

    // =================================================
    // SINGLE USER
    // =================================================

    if (
      userIds !== null &&
      userIds !== undefined
    ) {
      const res =
        await fetch(
          `${BASE_URL}/leave_requests/transactions?user_id=${userIds}&t=${Date.now()}`,
          {
            method: "GET",

            cache: "no-store",

            headers:
              noCacheHeaders,
          }
        );

      return res.json();
    }

    // =================================================
    // FALLBACK TO LOGGED-IN USER
    // =================================================

    const currentUser =
      JSON.parse(
        localStorage.getItem(
          "user"
        )
      );

    if (
      !currentUser ||
      !currentUser.id
    ) {
      console.error(
        "User not found"
      );

      return [];
    }

    const res =
      await fetch(
        `${BASE_URL}/leave_requests/transactions?user_id=${currentUser.id}&t=${Date.now()}`,
        {
          method: "GET",

          cache: "no-store",

          headers:
            noCacheHeaders,
        }
      );

    return res.json();
  };

// =====================================================
// HR LEAVE BALANCES
//
// OPTIMIZED:
// - Backend returns aggregated counts
// - user_ids[] parameter for filtering
// =====================================================

export const getHrLeaveBalances = async (params = {}) => {
  const user = JSON.parse(
    localStorage.getItem("user")
  );

  const queryParams = new URLSearchParams();

  queryParams.append(
    "page",
    params.page || 1
  );

  queryParams.append(
    "limit",
    params.limit || 15
  );

  queryParams.append(
    "search",
    params.search || ""
  );

  // Selected employee IDs
  if (
    Array.isArray(params.user_ids) &&
    params.user_ids.length > 0
  ) {
    params.user_ids.forEach((userId) => {
      queryParams.append(
        "user_ids[]",
        userId
      );
    });
  }

  queryParams.append(
    "t",
    Date.now()
  );

  const query =
    queryParams.toString();

  const res = await fetch(
    `${BASE_URL}/hr/leave_balances?${query}`,
    {
      method: "GET",
      cache: "no-store",
      headers: hrHeaders(user),
    }
  );

  const json = await res.json();

  if (!res.ok) {
    throw json;
  }

  return json;
};

// =====================================================
// GET PER-USER CYCLE ACTIVITY
// =====================================================

export const getHrCycleActivity =
  async (userId) => {
    const user =
      JSON.parse(
        localStorage.getItem(
          "user"
        )
      );

    const res =
      await fetch(
        `${BASE_URL}/hr/leave_balances/cycle_activity?user_id=${userId}&t=${Date.now()}`,
        {
          method: "GET",

          cache: "no-store",

          headers:
            hrHeaders(user),
        }
      );

    return res.json();
  };

// =====================================================
// UPDATE HR LEAVE BALANCE
// =====================================================

export const updateHrLeaveBalance =
  async (
    id,
    data
  ) => {
    const user =
      JSON.parse(
        localStorage.getItem(
          "user"
        )
      );

    const res =
      await fetch(
        `${BASE_URL}/hr/leave_balances/${id}`,
        {
          method: "PUT",

          cache: "no-store",

          headers: {
            "Content-Type":
              "application/json",

            ...hrHeaders(user),
          },

          body:
            JSON.stringify(
              data
            ),
        }
      );

    const json =
      await res.json();

    if (!res.ok) {
      throw json;
    }

    return json;
  };

// =====================================================
// HR LEAVE TRANSACTIONS
// =====================================================

export const getHrLeaveTransactions =
  async (params = {}) => {
    const user =
      JSON.parse(
        localStorage.getItem(
          "user"
        )
      );

    const query =
      new URLSearchParams(
        params
      ).toString();

    const res =
      await fetch(
        `${BASE_URL}/hr/leave_transactions?${query}&t=${Date.now()}`,
        {
          method: "GET",

          cache: "no-store",

          headers:
            hrHeaders(user),
        }
      );

    return res.json();
  };

// =====================================================
// CREATE HR LEAVE TRANSACTION
// =====================================================

export const createHrLeaveTransaction =
  async (data) => {
    const user =
      JSON.parse(
        localStorage.getItem(
          "user"
        )
      );

    const res =
      await fetch(
        `${BASE_URL}/hr/leave_transactions`,
        {
          method: "POST",

          cache: "no-store",

          headers: {
            "Content-Type":
              "application/json",

            ...hrHeaders(user),
          },

          body:
            JSON.stringify(
              data
            ),
        }
      );

    const json =
      await res.json();

    if (!res.ok) {
      throw json;
    }

    return json;
  };

// =====================================================
// APPLY COMP OFF
// =====================================================

export const applyCompOff =
  (data) =>
    fetch(
      `${BASE_URL}/leave_requests`,
      {
        method: "POST",

        headers: {
          "Content-Type":
            "application/json",
        },

        body:
          JSON.stringify(
            data
          ),
      }
    ).then((res) =>
      res.json()
    );

// =====================================================
// UPDATE LEAVE STATUS
// =====================================================

export const updateLeaveStatus =
  async (
    id,
    status,
    comment
  ) => {
    const user =
      JSON.parse(
        localStorage.getItem(
          "user"
        )
      );

    const res =
      await fetch(
        `${BASE_URL}/leave_requests/${id}/update_status?t=${Date.now()}`,
        {
          method: "POST",

          cache: "no-store",

          headers: {
            "Content-Type":
              "application/json",

            ...noCacheHeaders,
          },

          body:
            JSON.stringify({
              status,

              manager_comment:
                comment,

              approved_by:
                user.id,
            }),
        }
      );

    const json =
      await res.json();

    if (!res.ok) {
      throw json;
    }

    // ==========================================
    // FORCE REFRESH EVENT
    // ==========================================

    window.dispatchEvent(
      new Event(
        "leave-updated"
      )
    );

    return json;
  };

// =====================================================
// MANAGER ALL LEAVES
// =====================================================

export const getAllLeaves =
  async () => {
    const user =
      JSON.parse(
        localStorage.getItem(
          "user"
        )
      );

    const res =
      await fetch(
        `${BASE_URL}/manager/leaves?hr_manager_id=${user.id}&t=${Date.now()}`,
        {
          method: "GET",

          cache: "no-store",

          headers:
            noCacheHeaders,
        }
      );

    return res.json();
  };

// =====================================================
// UPLOAD HR LEAVE BALANCES
// =====================================================

export const uploadHrLeaveBalances =
  async (formData) => {
    const user =
      JSON.parse(
        localStorage.getItem(
          "user"
        )
      );

    const res =
      await fetch(
        `${BASE_URL}/hr/leave_balances/upload`,
        {
          method: "POST",

          headers: {
            "X-User-Email":
              user?.email || "",
          },

          body: formData,
        }
      );

    const json =
      await res.json();

    if (!res.ok) {
      throw json;
    }

    return json;
  };

// =====================================================
// UPDATE HR LEAVE TRANSACTION
// =====================================================

export const updateHrLeaveTransaction =
  async (
    id,
    data
  ) => {
    const user =
      JSON.parse(
        localStorage.getItem(
          "user"
        )
      );

    const res =
      await fetch(
        `${BASE_URL}/hr/leave_transactions/${id}`,
        {
          method: "PUT",

          cache: "no-store",

          headers: {
            "Content-Type":
              "application/json",

            ...hrHeaders(user),
          },

          body:
            JSON.stringify(
              data
            ),
        }
      );

    const json =
      await res.json();

    if (!res.ok) {
      throw json;
    }

    return json;
  };

// =====================================================
// EMPLOYEE SUMMARY
// =====================================================

export const getEmployeeLeaveSummary =
  async () => {
    const user =
      JSON.parse(
        localStorage.getItem(
          "user"
        )
      );

    const res =
      await fetch(
        `${BASE_URL}/leave_requests/employee_summary?hr_manager_id=${user.id}&t=${Date.now()}`,
        {
          method: "GET",

          cache: "no-store",

          headers:
            noCacheHeaders,
        }
      );

    return res.json();
  };

// =====================================================
// GET HR ACTIVITY LOGS
// =====================================================

export const getHrActivityLogs =
  async (params = {}) => {
    const user =
      JSON.parse(
        localStorage.getItem(
          "user"
        )
      );

    const query =
      new URLSearchParams(
        params
      ).toString();

    const res =
      await fetch(
        `${BASE_URL}/hr/activity_logs?${query}&t=${Date.now()}`,
        {
          method: "GET",

          cache: "no-store",

          headers:
            hrHeaders(user),
        }
      );

    return res.json();
  };

// =====================================================
// MONTHLY SUMMARY
// =====================================================

export const getMonthlyLeaveSummary =
  async (
    userId,
    year
  ) => {
    const res =
      await fetch(
        `${BASE_URL}/leave_requests/monthly_summary?user_id=${userId}&year=${year}&t=${Date.now()}`,
        {
          method: "GET",

          cache: "no-store",

          headers:
            noCacheHeaders,
        }
      );

    return res.json();
  };

// =====================================================
// HR USER ROLES
// =====================================================

export const getHrUserRoles =
  async () => {
    const user =
      JSON.parse(
        localStorage.getItem(
          "user"
        )
      );

    const res =
      await fetch(
        `${BASE_URL}/leave_user_roles?t=${Date.now()}`,
        {
          method: "GET",

          cache: "no-store",

          headers:
            hrHeaders(user),
        }
      );

    const json =
      await res.json();

    if (!res.ok) {
      throw json;
    }

    return json;
  };

// =====================================================
// CREATE HR USER ROLE
// =====================================================

export const createHrUserRole =
  async (data) => {
    const user =
      JSON.parse(
        localStorage.getItem(
          "user"
        )
      );

    const res =
      await fetch(
        `${BASE_URL}/leave_user_roles`,
        {
          method: "POST",

          headers: {
            "Content-Type":
              "application/json",

            ...hrHeaders(user),
          },

          body:
            JSON.stringify(
              data
            ),
        }
      );

    const json =
      await res.json();

    if (!res.ok) {
      throw json;
    }

    return json;
  };

// =====================================================
// UPDATE HR USER ROLE
// =====================================================

export const updateHrUserRole =
  async (
    userId,
    data
  ) => {
    const user =
      JSON.parse(
        localStorage.getItem(
          "user"
        )
      );

    const res =
      await fetch(
        `${BASE_URL}/leave_user_roles/${userId}`,
        {
          method: "PATCH",

          headers: {
            "Content-Type":
              "application/json",

            ...hrHeaders(user),
          },

          body:
            JSON.stringify(
              data
            ),
        }
      );

    const json =
      await res.json();

    if (!res.ok) {
      throw json;
    }

    return json;
  };

// =====================================================
// DELETE HR USER ROLE
// =====================================================

export const deleteHrUserRole =
  async (id) => {
    const user =
      JSON.parse(
        localStorage.getItem(
          "user"
        )
      );

    const res =
      await fetch(
        `${BASE_URL}/leave_user_roles/${id}`,
        {
          method: "DELETE",

          headers:
            hrHeaders(user),
        }
      );

    const json =
      await res.json();

    if (!res.ok) {
      throw json;
    }

    return json;
  };

// =====================================================
// DELETE DUPLICATE BALANCES
// =====================================================

export const deleteDuplicateBalances =
  async () => {
    const user =
      JSON.parse(
        localStorage.getItem(
          "user"
        )
      );

    const res =
      await fetch(
        `${BASE_URL}/hr/leave_balances/delete_duplicates`,
        {
          method: "POST",

          cache: "no-store",

          headers:
            hrHeaders(user),
        }
      );

    const json =
      await res.json();

    if (!res.ok) {
      throw json;
    }

    return json;
  };

// =====================================================
// GET USER TRANSACTIONS
//
// FILTERS:
// - user_id
// - emp_code
// - filter_type
// - cycle_date
// - pagination
// =====================================================

// =====================================================
// RUN MONTHLY LEAVE CREDIT
// =====================================================

export const runMonthlyLeaveCredit =
  async () => {

    const user =
      JSON.parse(
        localStorage.getItem("user")
      );

    const res =
      await fetch(
        `${BASE_URL}/hr/leave_balances/run_monthly_leave_credit`,
        {
          method: "POST",

          cache: "no-store",

          headers:
            hrHeaders(user),
        }
      );

    const json =
      await res.json();

    if (!res.ok) {
      throw json;
    }

    return json;
  };
export const getUserTransactions =
  async (params = {}) => {
    const user =
      JSON.parse(
        localStorage.getItem(
          "user"
        )
      );

    const queryObj = {
      page:
        params.page || 1,

      limit:
        params.limit || 10,

      filter_type:
        params.filter_type ||
        "all",

      t: Date.now(),
    };

    // =================================================
    // USER IDENTIFIER
    // =================================================

    if (
      params.user_id
    ) {
      queryObj.user_id =
        params.user_id;
    } else if (
      params.emp_code
    ) {
      queryObj.emp_code =
        params.emp_code;
    }

    // =================================================
    // CYCLE DATE
    // =================================================

    if (
      params.cycle_date
    ) {
      queryObj.cycle_date =
        params.cycle_date;
    }

    const query =
      new URLSearchParams(
        queryObj
      ).toString();

    const res =
      await fetch(
        `${BASE_URL}/hr/leave_transactions/user_transactions?${query}`,
        {
          method: "GET",

          cache: "no-store",

          headers:
            hrHeaders(user),
        }
      );

    return res.json();
  };